// Bolt tracers and impact flashes: the whole VFX budget of the coil war,
// per the audit's recipe — emissive geometry and additive sprites, no
// sims, everything readable at a glance. A coil bolt is teal (the aether
// leaving in a straight line); a warden bolt is furnace amber. Entries
// live ~130 ms and are driven purely by BoltMessages — cosmetic, so
// deliberately untested per house law.

import type { BoltMessage } from "@bebop/shared";
import * as THREE from "three";
import { Tracker } from "./common";

const TRACER_LIFE_S = 0.14;
const FLASH_LIFE_S = 0.24;

const COIL_COLOR = 0x62d6c4;
const WARDEN_COLOR = 0xe8913a;

interface Tracer {
  mesh: THREE.Mesh;
  material: THREE.MeshBasicMaterial;
  age: number;
}

interface Flash {
  sprite: THREE.Sprite;
  material: THREE.SpriteMaterial;
  age: number;
}

export class TracerLayer {
  readonly group = new THREE.Group();
  private tracker = new Tracker();
  private beam: THREE.BufferGeometry;
  private flashTexture: THREE.Texture;
  private tracers: Tracer[] = [];
  private flashes: Flash[] = [];

  constructor() {
    const t = this.tracker;
    // A unit beam along +z, scaled per bolt: thin, square, cheap.
    this.beam = t.geo(new THREE.BoxGeometry(0.35, 0.35, 1));
    this.beam.translate(0, 0, 0.5);

    const canvas = document.createElement("canvas");
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      const g = ctx.createRadialGradient(32, 32, 2, 32, 32, 32);
      g.addColorStop(0, "rgba(255, 244, 214, 1)");
      g.addColorStop(0.35, "rgba(255, 196, 107, 0.8)");
      g.addColorStop(1, "rgba(255, 196, 107, 0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 64, 64);
    }
    this.flashTexture = t.tex(new THREE.CanvasTexture(canvas));
  }

  /** A bolt crossed the sky: lay the beam, flash the impact. */
  spawn(bolt: BoltMessage): void {
    const color = bolt.source === "coil" ? COIL_COLOR : WARDEN_COLOR;
    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.95,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(this.beam, material);
    const from = new THREE.Vector3(bolt.sx, bolt.sy, bolt.sz);
    const to = new THREE.Vector3(bolt.tx, bolt.ty, bolt.tz);
    const length = from.distanceTo(to);
    mesh.position.copy(from);
    mesh.lookAt(to);
    mesh.scale.set(1, 1, Math.max(1, length));
    this.group.add(mesh);
    this.tracers.push({ mesh, material, age: 0 });

    if (bolt.hit) {
      const flashMaterial = new THREE.SpriteMaterial({
        map: this.flashTexture,
        color,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const sprite = new THREE.Sprite(flashMaterial);
      sprite.position.copy(to);
      sprite.scale.set(9, 9, 1);
      this.group.add(sprite);
      this.flashes.push({ sprite, material: flashMaterial, age: 0 });
    }
  }

  tick(dt: number): void {
    for (let i = this.tracers.length - 1; i >= 0; i--) {
      const tracer = this.tracers[i] as Tracer;
      tracer.age += dt;
      const f = tracer.age / TRACER_LIFE_S;
      if (f >= 1) {
        this.group.remove(tracer.mesh);
        tracer.material.dispose();
        this.tracers.splice(i, 1);
      } else {
        tracer.material.opacity = 0.95 * (1 - f);
      }
    }
    for (let i = this.flashes.length - 1; i >= 0; i--) {
      const flash = this.flashes[i] as Flash;
      flash.age += dt;
      const f = flash.age / FLASH_LIFE_S;
      if (f >= 1) {
        this.group.remove(flash.sprite);
        flash.material.dispose();
        this.flashes.splice(i, 1);
      } else {
        flash.material.opacity = 1 - f;
        flash.sprite.scale.setScalar(9 + f * 10);
      }
    }
  }

  dispose(): void {
    for (const tracer of this.tracers) {
      this.group.remove(tracer.mesh);
      tracer.material.dispose();
    }
    for (const flash of this.flashes) {
      this.group.remove(flash.sprite);
      flash.material.dispose();
    }
    this.tracers = [];
    this.flashes = [];
    this.tracker.dispose();
  }
}
